import { Box, Button, Stack, Typography } from "@mui/material";
import { FC, useContext, useState } from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { AxiosResponse } from "axios";

import {
  FormValues,
  OverrideAnnualKpiDialog
} from "pages/Kpis/KpisDialogs/OverrideAnnualKpiDialog";
import { API_URL_KPIS } from "constants/api_urls";
import { AxiosContext } from "contexts/AxiosContext";
import { SnackbarQueueContext } from "contexts/SnackbarQueueContext";
import { MeasurementUpdates, Target } from "interfaces/kpis";
import { makeQueryString } from "core/makeQueryString";
import { ViewEvidenceAnnualKpiDialog } from "pages/Kpis/KpisDialogs/ViewEvidenceAnnualKpiDialog";

export interface Override {
  justification: string;
  userName: string;
  date: string;
}

export interface Report {
  link: string;
  title: string;
  userName: string;
  date: string;
}

interface TargetPostPayload {
  override_justification: string;
}

interface Props {
  hasReport: boolean;
  hasOverride: boolean;
  hasMeasurements: boolean;
  machineName: string;
  kpiId: number;
  targetId: number;
  siteId: number;
  override: Override | undefined;
  report: Report | undefined;
  measurements: MeasurementUpdates[] | undefined;
}

export const TurbineRow: FC<Props> = ({
  hasReport,
  hasOverride,
  hasMeasurements,
  machineName,
  kpiId,
  targetId,
  siteId,
  override,
  report,
  measurements
}): JSX.Element => {
  const queryClient = useQueryClient();
  const { axios } = useContext(AxiosContext)!;

  const [showOverrideDialog, setShowOverrideDialog] = useState(false);
  const [showViewEvidenceDialog, setShowViewEvidenceDialog] = useState(false);
  const { addSnackbarToQueue } = useContext(SnackbarQueueContext);

  const overrideAnnualKpiMutation = useMutation(
    ["override-annual-kpi-target"],
    {
      mutationFn: async (payload: TargetPostPayload) => {
        const { override_justification } = payload;

        return axios.patch<TargetPostPayload, AxiosResponse<Target>>(
          `${API_URL_KPIS}/targets/${targetId}`,
          {
            override_justification
          }
        );
      },
      onSuccess: async () => {
        setShowOverrideDialog(false);

        await queryClient.invalidateQueries([
          "kpis",
          "targets",
          makeQueryString({ kpi_id: kpiId, site_id: siteId })
        ]);

        await queryClient.invalidateQueries(["kpis", "progress"]);
        await queryClient.invalidateQueries(["kpis", "targets"]);
        await queryClient.invalidateQueries([
          "kpis",
          "machines",
          "with_activity",
          "all"
        ]);

        addSnackbarToQueue({
          message: "KPI Target successfully overridden.",
          severity: "success"
        });
      },
      onError: () => {
        addSnackbarToQueue({
          message: "Failed to override KPI Target.",
          severity: "error"
        });
      }
    }
  );

  const hasEvidence = hasReport || hasOverride || hasMeasurements;

  const handleView = (): void => {
    setShowViewEvidenceDialog(true);
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        gap={1}
        justifyContent="space-between"
        p={0.5}
      >
        <Stack direction="row" gap={2} alignItems="center">
          <Stack alignContent="center">
            {hasEvidence ? (
              <CheckCircleIcon color="primary" />
            ) : (
              <CheckCircleIcon opacity={0} />
            )}
          </Stack>
          <Typography variant="body2">{machineName}</Typography>
        </Stack>

        <Box>
          {hasEvidence ? (
            <Button variant="outlined" color="primary" onClick={handleView}>
              View
            </Button>
          ) : (
            <Button
              variant="outlined"
              color="warning"
              onClick={() => {
                setShowOverrideDialog(true);
              }}
            >
              Override
            </Button>
          )}
        </Box>
      </Stack>

      {showOverrideDialog && (
        <OverrideAnnualKpiDialog
          isOpen={showOverrideDialog}
          onClose={() => setShowOverrideDialog(false)}
          onConfirm={async (formValues: FormValues): Promise<void> => {
            try {
              await overrideAnnualKpiMutation.mutate(formValues);
              setShowViewEvidenceDialog(false);
            } catch (err) {
              console.info(err);
            }
          }}
        />
      )}

      {showViewEvidenceDialog &&
        (hasReport || hasOverride || hasMeasurements) && (
          <ViewEvidenceAnnualKpiDialog
            isOpen={showViewEvidenceDialog}
            onClose={() => setShowViewEvidenceDialog(false)}
            targetId={targetId}
            override={override}
            report={report}
            measurements={measurements}
          />
        )}
    </>
  );
};
